import { useEffect } from 'react';
import {
  type Location,
  type NavigateFunction,
  useLocation,
  useNavigate,
} from 'react-router';

import { APP_CONST } from '@/constant';
import { storage } from '@/package';
import { useAuthStore, useTenantStore } from '@/store';
import type { UserRole } from '@/type';

const PATH_PATTERN = {
  NEW_PASSWORD: /\/auth\/(new-password)/,
  PUBLIC_PATH_LIST:
    /(\/auth\/(invitation|signup|service-preparation-notice|send-verification-code|verification-email|onboarding|reset-password|new-password|password-change-complete))|\/landing/,
  SIGNIN_TENANT_PATH_LIST: /\/auth\/((sign(i|I)n)|(tenant-selection))/,
} as const;

const extractSearchParam = (
  searchParams: URLSearchParams,
  key: string,
): Nullish<string> => {
  if (!searchParams.has(key)) return null;

  const value = searchParams.get(key);
  searchParams.delete(key);
  return value;
};

/**
 * 현재 경로를 기반으로 리다이렉트 경로를 계산
 */
const calculateRedirectPath = (
  location: Location,
  searchParams: URLSearchParams,
  storedRedirect: Nullish<string>,
): string => {
  const currentPath = location.pathname;
  const isSigninOrTenantSelection =
    PATH_PATTERN.SIGNIN_TENANT_PATH_LIST.test(currentPath);
  const isRoot = currentPath === '/';

  if (!isSigninOrTenantSelection && !isRoot) {
    return currentPath + (searchParams.size > 0 ? `?${searchParams}` : '');
  }

  return storedRedirect || '/';
};

const handleUnauthenticatedUser = (
  location: Location,
  navigate: NavigateFunction,
  isCognitoAuthenticated: boolean,
  isTenantAuthenticated: boolean,
) => {
  const searchParam = new URLSearchParams(location.search);
  const newSearchParam = new URLSearchParams();

  const storedRedirect = storage.local.get<string>(
    APP_CONST.KEYS.STORAGE_REDIRECT_URL,
  );
  const urlRedirect = extractSearchParam(searchParam, 'redirect');
  const redirectPath = calculateRedirectPath(
    location,
    searchParam,
    storedRedirect ?? urlRedirect,
  );

  newSearchParam.append('redirect', redirectPath);

  if (!isTenantAuthenticated && isCognitoAuthenticated) {
    navigate(`/auth/tenant-selection?${newSearchParam}`, { replace: true });
    return;
  }

  if (!isCognitoAuthenticated) {
    const code = extractSearchParam(searchParam, 'code');

    if (code) {
      newSearchParam.append('code', code);
      navigate(`/auth/signin?${newSearchParam}`, { replace: true });
      return;
    }

    if (location.pathname === '/') {
      navigate(`/landing?${newSearchParam}`, { replace: true });
      return;
    }

    navigate(`/auth/signin?${newSearchParam}`, { replace: true });
  }
};

const handleAuthenticatedUser = (
  location: Location,
  navigate: NavigateFunction,
  userRole: UserRole,
) => {
  const searchParam = new URLSearchParams(location.search);
  const redirect = searchParam.get('redirect');

  if (redirect) {
    navigate(redirect, { replace: true });
    return;
  }

  if (
    location.pathname === '/' ||
    location.pathname === '/auth/tenant-selection'
  ) {
    navigate(APP_CONST.GET_REDIRECT_PATH_BY_ROLE(userRole), { replace: true });
  }

  storage.local.remove(APP_CONST.KEYS.STORAGE_REDIRECT_URL);
};

const useAuthProvider = () => {
  const { user, isCognitoAuthenticated, isTenantAuthenticated } =
    useAuthStore();
  const { currentTenantID: currentTenantId } = useTenantStore();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (PATH_PATTERN.NEW_PASSWORD.test(location.pathname)) {
      return;
    }

    const isAuthenticated = isTenantAuthenticated() && isCognitoAuthenticated();

    if (!isAuthenticated) {
      if (PATH_PATTERN.PUBLIC_PATH_LIST.test(location.pathname)) {
        return;
      }

      handleUnauthenticatedUser(
        location,
        navigate,
        isCognitoAuthenticated(),
        isTenantAuthenticated(),
      );
    } else {
      handleAuthenticatedUser(location, navigate, user.role);
    }
  }, [user, currentTenantId, location.pathname]);
};

export default useAuthProvider;
